import type {
  PublicShowcaseApplication,
  PublicShowcasePreview,
} from '../shared/types'

/**
 * The public surface is mounted under two different paths — `apps.sasid.ai/`
 * and `health.sasid.ai/showcase` — and the server answers every path with the
 * same SPA shell. So a product page needs no server route, only agreement
 * about where the prefix ends.
 */
export const SHOWCASE_BASE = () =>
  window.location.pathname.startsWith('/showcase') ? '/showcase' : ''

export const productPath = (id: string) => `${SHOWCASE_BASE()}/p/${id}`

/** The product id in the current URL, or undefined on the index. */
export function readRoute(pathname: string): string | undefined {
  const match = /\/p\/([a-z0-9-]{1,64})\/?$/i.exec(pathname)
  return match ? match[1] : undefined
}

/**
 * A metric worth showing.
 *
 * The server already drops metrics it cannot source, but "sourced" and "worth
 * putting on a portfolio" are different bars: nine products currently report a
 * real, correctly-measured zero. Publishing "0 appointments booked" next to a
 * product is worse than publishing nothing, and it is the reader's first
 * impression of every product below it too.
 */
export function sellableMetrics(application: PublicShowcaseApplication) {
  return application.productMetrics.filter(
    (metric) => typeof metric.value === 'number' && metric.value > 0,
  )
}

export function formatMetric(value: number, unit: string): string {
  if (unit === 'percent') return `${Math.round(value)}%`
  if (unit === 'currency-minor') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value / 100)
  }
  if (unit === 'seconds') return `${Math.round(value)}s`
  return new Intl.NumberFormat('en-US', {
    notation: value >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value)
}

export function hostOf(application: PublicShowcaseApplication): string {
  const primary =
    application.domains.find((domain) => domain.primary) ?? application.domains[0]
  if (primary) return primary.hostname
  try {
    return new URL(application.homepage).hostname.replace(/^www\./, '')
  } catch {
    return application.homepage
  }
}

export const heroPreview = (
  application: PublicShowcaseApplication,
): PublicShowcasePreview | undefined => application.previews[0]

/**
 * What a card says about a product.
 *
 * Descriptions are written for the registry, not for a tile, and they run from
 * eight words to sixty. Clamping to two lines cut the long ones mid-clause —
 * "finds those peopl…" — which reads as a broken page rather than a summary.
 *
 * The opening sentence is the positioning line in every one of them. Where that
 * sentence is itself an essay, it is almost always a claim followed by a colon
 * and the mechanism ("An AI agent that runs the whole outbound pipeline:
 * learns what you sell, ranks who buys it, ..."), and the claim alone is the
 * better card line. The full text is still on the product page.
 */
const CARD_MAX = 130

export function cardLine(text: string): string {
  const trimmed = text.trim()
  const sentenceEnd = /[.!?](\s|$)/.exec(trimmed)
  let line =
    sentenceEnd && sentenceEnd.index + 1 < trimmed.length * 0.9
      ? trimmed.slice(0, sentenceEnd.index + 1)
      : trimmed
  if (line.length > CARD_MAX) {
    const colon = line.indexOf(':')
    if (colon >= 20) line = `${line.slice(0, colon)}.`
  }
  return line
}
